/**
 * Spells and prayers, written in the tools.
 *
 * Everything about one on a single page: how it looks, what it asks for, what
 * it costs, what it is cast at, and what it does. What it does is the fighting
 * part, which has fields of its own, and then anything at all in the language
 * acts and conversations use, so a spell can hand something over, set a flag,
 * move a quest along or put somebody somewhere, and whatever acts learn to do,
 * spells can do too.
 *
 * Saving sends the whole thing to the world, which checks it, keeps it, writes
 * it down and sends everybody's book round again.
 */

import { useMemo, useState } from 'react';
import type {
  ItemDefinitionWire,
  ModifierWire,
  ObjectDefinitionWire,
  PrayerWire,
  QuestWire,
  SpellTargetWire,
  SpellWire,
  StatusWire,
} from '@inverse/protocol';
import { MODIFIER_NAMES } from '@inverse/protocol';
import type { ModelWire, ProjectileWire, SpellShapeWire } from '@inverse/protocol';
import {
  CHANCE_PRESETS,
  checkFormula,
  evaluateFormula,
  FORMULA_FUNCTIONS,
  FORMULA_NAMES,
  spellArea,
} from '@inverse/common';
import type { AdminCommand } from '../Admin';
import { Glyph } from '../Glyph';
import { GLYPH_NAMES } from '../glyphs';
import { Conditions, Effects } from './Dialogue';
import { ItemPicker } from './ItemPicker';
import { SoundPicker } from './Sounds';

interface World {
  models?: readonly ModelWire[];
  items: readonly ItemDefinitionWire[];
  quests: readonly QuestWire[];
  characters: readonly { id: number; name: string }[];
  objects: readonly ObjectDefinitionWire[];
  shops: readonly { id: number; name: string }[];
}

const TARGETS: readonly { value: SpellTargetWire; label: string }[] = [
  { value: 'enemy', label: 'An enemy' },
  { value: 'friend', label: 'A friend' },
  { value: 'self', label: 'The caster' },
  { value: 'tile', label: 'A place on the ground' },
  { value: 'item', label: 'Something in the bag' },
];

const ELEMENTS = ['none', 'air', 'water', 'earth', 'fire'];

/** The looks the client draws on its own, and a model from the library. */
const LOOKS: readonly { value: string; label: string }[] = [
  { value: '', label: 'Nothing' },
  { value: 'air', label: 'A spark' },
  { value: 'water', label: 'A drop' },
  { value: 'earth', label: 'A clod' },
  { value: 'fire', label: 'A flame' },
  { value: 'bolt', label: 'A bolt' },
  { value: 'arrow', label: 'An arrow' },
  { value: 'model', label: 'A model from the library' },
];

const SHAPES: readonly { value: SpellShapeWire; label: string }[] = [
  { value: 'square', label: 'Square' },
  { value: 'plus', label: 'Plus' },
  { value: 'diamond', label: 'Diamond' },
  { value: 'circle', label: 'Circle' },
  { value: 'line', label: 'Line from the caster' },
];

/** Numbers to work a formula out with, so the tools can say what one comes to. */
const EXAMPLE: Readonly<Record<string, number>> = {
  magic: 50,
  ranged: 40,
  attack: 40,
  strength: 40,
  defence: 40,
  constitution: 40,
  faith: 30,
  combat: 55,
  magicBonus: 10,
  magicDamage: 0,
  targetLevel: 30,
  targetDefence: 25,
  targetMagic: 15,
  targetMagicDefence: 5,
  targetHealth: 40,
  targetMaxHealth: 60,
  hit: 1,
  damage: 6,
};

const STATS = Object.keys(MODIFIER_NAMES);

function blankSpell(book: string): SpellWire {
  return {
    id: 0,
    name: 'New Spell',
    description: '',
    book,
    order: 0,
    icon: { glyph: 'orb', colour: '#8c96ff' },
    level: 1,
    experience: 0,
    mana: 0,
    components: [],
    requires: [],
    target: 'enemy',
    range: 10,
    radius: 0,
    shape: 'square',
    autocast: false,
    tier: '',
    instant: false,
    speed: 5,
    projectile: { look: '', model: '', speed: 10, size: 1, arc: 0 },
    accuracy: '',
    sounds: { cast: '', impact: '' },
    heals: 0,
    restores: [],
    effects: [],
  };
}

function blankPrayer(category: string): PrayerWire {
  return {
    id: 0,
    name: 'New Prayer',
    description: '',
    category,
    order: 0,
    icon: { glyph: 'sun', colour: '#ddb43f' },
    level: 1,
    group: '',
    drain: 6,
    requires: [],
    modifiers: [],
  };
}

/** A number field. */
function Num({
  label,
  value,
  min = 0,
  max,
  step = 1,
  onChange,
}: {
  label: string;
  value: number;
  min?: number;
  max?: number;
  step?: number;
  onChange: (value: number) => void;
}): JSX.Element {
  return (
    <label className="editor-field">
      <span>{label}</span>
      <input
        type="number"
        value={value}
        min={min}
        max={max}
        step={step}
        onChange={(event) => onChange(Number(event.target.value) || 0)}
      />
    </label>
  );
}

/** A line of text, with the names already in use offered. */
function Text({
  label,
  value,
  offered,
  max = 48,
  onChange,
}: {
  label: string;
  value: string;
  offered?: readonly string[];
  max?: number;
  onChange: (value: string) => void;
}): JSX.Element {
  const list = offered ? `offered-${label.replace(/\W/g, '')}` : undefined;
  return (
    <label className="editor-field">
      <span>{label}</span>
      <input
        type="text"
        value={value}
        maxLength={max}
        list={list}
        onChange={(event) => onChange(event.target.value)}
      />
      {offered ? (
        <datalist id={list}>
          {offered.map((one) => (
            <option key={one} value={one} />
          ))}
        </datalist>
      ) : null}
    </label>
  );
}

/**
 * A formula, with the ones worth having ready in a list beside it.
 *
 * Checked as it is written, and worked out for a caster of middling level
 * against something a little weaker, so whoever writes one sees what it comes
 * to before anybody casts it.
 */
function FormulaField({
  label,
  value,
  empty,
  onChange,
}: {
  label: string;
  value: string;
  /** What leaving it empty means, for a formula that may be left empty. */
  empty?: string;
  onChange: (value: string) => void;
}): JSX.Element {
  const problem = value.trim() ? checkFormula(value) : null;
  const comes =
    value.trim() && !problem ? Math.min(1, Math.max(0, evaluateFormula(value, EXAMPLE))) : null;
  const preset = CHANCE_PRESETS.find((one) => one.formula === value.trim());

  return (
    <div className="magic-box">
      <label className="editor-field">
        <span>{label}</span>
        <select
          value={preset ? preset.formula : value.trim() ? 'own' : ''}
          onChange={(event) => {
            if (event.target.value !== 'own') onChange(event.target.value);
          }}
        >
          {empty !== undefined ? <option value="">{empty}</option> : null}
          {CHANCE_PRESETS.map((one) => (
            <option key={one.label} value={one.formula}>
              {one.label}
            </option>
          ))}
          <option value="own">Written by hand</option>
        </select>
      </label>
      <input
        type="text"
        className="magic-formula"
        value={value}
        maxLength={400}
        placeholder={empty ?? 'hit * clamp(magic / 99, 0, 1)'}
        onChange={(event) => onChange(event.target.value)}
      />
      {problem ? <p className="hint warning">{problem}</p> : null}
      {comes !== null ? (
        <p className="hint">
          At Magic 50 against a level 30 target with 25 Defence: {Math.round(comes * 100)}%.
        </p>
      ) : null}
      <details className="magic-names">
        <summary>What a formula can use</summary>
        <ul>
          {Object.entries(FORMULA_NAMES).map(([name, meaning]) => (
            <li key={name}>
              <code>{name}</code> {meaning}
            </li>
          ))}
        </ul>
        <p className="hint">
          Functions: {FORMULA_FUNCTIONS.join(', ')}. Also + - * / ^, comparisons such as &gt;=, and{' '}
          <code>a ? b : c</code>. It should come to between nought and one.
        </p>
      </details>
    </div>
  );
}

/** The ground a spell covers, drawn round where it lands, with the caster below. */
function ShapePreview({ shape, size }: { shape: SpellShapeWire; size: number }): JSX.Element {
  const across = 11;
  const middle = { tx: 5, tz: 4, plane: 0 };
  const caster = { tx: 5, tz: 9, plane: 0 };
  const covered = new Set(
    spellArea(shape, size, middle, caster).map((one) => `${one.tx},${one.tz}`),
  );
  const cells: JSX.Element[] = [];
  for (let tz = 0; tz < across; tz++) {
    for (let tx = 0; tx < across; tx++) {
      const key = `${tx},${tz}`;
      const kind =
        tx === caster.tx && tz === caster.tz
          ? 'caster'
          : tx === middle.tx && tz === middle.tz
            ? 'aimed'
            : covered.has(key)
              ? 'covered'
              : '';
      cells.push(<span key={key} className={`magic-cell ${kind}`} />);
    }
  }
  return (
    <div className="magic-shape" title="Where it lands is outlined; the caster is the dot below">
      {cells}
    </div>
  );
}

/** The picture: one from the library, a colour, and a ground if wanted. */
function IconPicker({
  icon,
  onChange,
}: {
  icon: SpellWire['icon'];
  onChange: (icon: SpellWire['icon']) => void;
}): JSX.Element {
  return (
    <>
      <div className="magic-glyphs">
        {GLYPH_NAMES.map((name) => (
          <button
            key={name}
            type="button"
            title={name}
            className={icon.glyph === name ? 'magic-glyph on' : 'magic-glyph'}
            onClick={() => onChange({ ...icon, glyph: name })}
          >
            <Glyph icon={{ glyph: name, colour: icon.colour }} />
          </button>
        ))}
      </div>
      <div className="editor-brushes">
        <label className="editor-field">
          <span>Colour</span>
          <input
            type="color"
            value={icon.colour}
            onChange={(event) => onChange({ ...icon, colour: event.target.value })}
          />
        </label>
        <label className="editor-field">
          <span>Ground</span>
          <input
            type="checkbox"
            checked={icon.ground !== undefined}
            onChange={(event) =>
              onChange(
                event.target.checked
                  ? { ...icon, ground: '#1c2430' }
                  : { glyph: icon.glyph, colour: icon.colour },
              )
            }
          />
          {icon.ground !== undefined ? (
            <input
              type="color"
              value={icon.ground}
              onChange={(event) => onChange({ ...icon, ground: event.target.value })}
            />
          ) : null}
        </label>
      </div>
    </>
  );
}

/** What a prayer or a lasting effect changes, as rows. */
function ModifierRows({
  held,
  onChange,
}: {
  held: readonly ModifierWire[];
  onChange: (modifiers: ModifierWire[]) => void;
}): JSX.Element {
  return (
    <>
      {held.map((one, at) => (
        <div className="editor-brushes" key={at}>
          <select
            value={one.stat}
            onChange={(event) =>
              onChange(
                held.map((entry, index) =>
                  index === at ? { ...entry, stat: event.target.value } : entry,
                ),
              )
            }
          >
            {STATS.map((stat) => (
              <option key={stat} value={stat}>
                {MODIFIER_NAMES[stat]}
              </option>
            ))}
          </select>
          <input
            type="number"
            value={one.percent}
            min={-100}
            max={500}
            onChange={(event) =>
              onChange(
                held.map((entry, index) =>
                  index === at
                    ? { ...entry, percent: Math.round(Number(event.target.value) || 0) }
                    : entry,
                ),
              )
            }
          />
          <span>%</span>
          <button
            type="button"
            className="editor-tool"
            onClick={() => onChange(held.filter((_, index) => index !== at))}
          >
            ✕
          </button>
        </div>
      ))}
      <button
        type="button"
        className="editor-tab"
        onClick={() => onChange([...held, { stat: 'defence', percent: 5 }])}
      >
        Add A Change
      </button>
    </>
  );
}

/** A lasting effect, written in place. */
function StatusFields({
  status,
  onChange,
}: {
  status: StatusWire;
  onChange: (status: StatusWire) => void;
}): JSX.Element {
  return (
    <div className="magic-box">
      <Text
        label="Called"
        value={status.name}
        max={32}
        onChange={(name) => onChange({ ...status, name })}
      />
      <Num
        label="Lasts (ticks)"
        value={status.ticks}
        min={1}
        max={6000}
        onChange={(ticks) => onChange({ ...status, ticks })}
      />
      <div className="editor-brushes">
        <label className="editor-field">
          <span>Cannot act</span>
          <input
            type="checkbox"
            checked={status.stun}
            onChange={(event) => onChange({ ...status, stun: event.target.checked })}
          />
        </label>
        <label className="editor-field">
          <span>Cannot move</span>
          <input
            type="checkbox"
            checked={status.bind}
            onChange={(event) => onChange({ ...status, bind: event.target.checked })}
          />
        </label>
        <label className="editor-field">
          <span>Good for them</span>
          <input
            type="checkbox"
            checked={status.helpful}
            onChange={(event) => onChange({ ...status, helpful: event.target.checked })}
          />
        </label>
      </div>
      <div className="editor-brushes">
        <Num
          label="Poison damage"
          value={status.poison}
          max={100}
          onChange={(poison) => onChange({ ...status, poison })}
        />
        <Num
          label="Every (ticks)"
          value={status.every}
          min={1}
          max={100}
          onChange={(every) => onChange({ ...status, every })}
        />
      </div>
      <FormulaField
        label="Chance it takes hold"
        value={status.chance}
        onChange={(chance) => onChange({ ...status, chance })}
      />
      <h5>What it changes while it lasts</h5>
      <ModifierRows
        held={status.modifiers}
        onChange={(modifiers) => onChange({ ...status, modifiers })}
      />
    </div>
  );
}

/** What flies from the caster: a look or a model, its colours, and how it travels. */
function ProjectileFields({
  projectile,
  models = [],
  onChange,
}: {
  projectile: ProjectileWire;
  models?: readonly ModelWire[];
  onChange: (projectile: ProjectileWire) => void;
}): JSX.Element {
  const look = projectile.model ? 'model' : projectile.look;
  return (
    <div className="magic-box">
      <label className="editor-field">
        <span>Look</span>
        <select
          value={LOOKS.some((one) => one.value === look) ? look : ''}
          onChange={(event) =>
            onChange(
              event.target.value === 'model'
                ? { ...projectile, look: '', model: models[0]?.name ?? '' }
                : { ...projectile, look: event.target.value, model: '' },
            )
          }
        >
          {LOOKS.map((one) => (
            <option key={one.value} value={one.value}>
              {one.label}
            </option>
          ))}
        </select>
      </label>
      {look === 'model' ? (
        <label className="editor-field">
          <span>Model</span>
          <select
            value={projectile.model}
            onChange={(event) => onChange({ ...projectile, model: event.target.value })}
          >
            {models.map((one) => (
              <option key={one.name} value={one.name}>
                {one.title || one.name}
              </option>
            ))}
          </select>
        </label>
      ) : null}
      {look !== '' ? (
        <>
          <div className="editor-brushes">
            <label className="editor-field">
              <span>Colour</span>
              <input
                type="checkbox"
                checked={projectile.colour !== undefined}
                onChange={(event) =>
                  onChange(
                    event.target.checked
                      ? { ...projectile, colour: '#8c96ff' }
                      : { ...projectile, colour: undefined },
                  )
                }
              />
              {projectile.colour !== undefined ? (
                <input
                  type="color"
                  value={projectile.colour}
                  onChange={(event) => onChange({ ...projectile, colour: event.target.value })}
                />
              ) : null}
            </label>
            <label className="editor-field">
              <span>Glow</span>
              <input
                type="checkbox"
                checked={projectile.colour2 !== undefined}
                onChange={(event) =>
                  onChange(
                    event.target.checked
                      ? { ...projectile, colour2: '#ffffff' }
                      : { ...projectile, colour2: undefined },
                  )
                }
              />
              {projectile.colour2 !== undefined ? (
                <input
                  type="color"
                  value={projectile.colour2}
                  onChange={(event) => onChange({ ...projectile, colour2: event.target.value })}
                />
              ) : null}
            </label>
          </div>
          <div className="editor-brushes">
            <Num
              label="Speed (tiles a second)"
              value={projectile.speed}
              min={1}
              max={60}
              onChange={(speed) => onChange({ ...projectile, speed })}
            />
            <Num
              label="Size"
              value={projectile.size}
              min={0.1}
              max={8}
              step={0.1}
              onChange={(size) => onChange({ ...projectile, size })}
            />
            <Num
              label="Arc (tiles high)"
              value={projectile.arc}
              min={0}
              max={8}
              step={0.1}
              onChange={(arc) => onChange({ ...projectile, arc })}
            />
          </div>
        </>
      ) : null}
    </div>
  );
}

/** The list down the side, grouped by book or kind, with a way to start a new one. */
function MagicList<T extends { id: number; name: string }>({
  entries,
  groupOf,
  chosen,
  onOpen,
  onNew,
  noun,
}: {
  entries: readonly T[];
  groupOf: (entry: T) => string;
  chosen: number;
  onOpen: (entry: T) => void;
  onNew: () => void;
  noun: string;
}): JSX.Element {
  const groups = useMemo(() => {
    const held = new Map<string, T[]>();
    for (const entry of entries) {
      const group = groupOf(entry);
      held.set(group, [...(held.get(group) ?? []), entry]);
    }
    return [...held.entries()];
  }, [entries, groupOf]);

  return (
    <div className="editor-list">
      <button type="button" className="editor-tab" onClick={onNew}>
        New {noun}
      </button>
      {groups.map(([group, list]) => (
        <div className="editor-group" key={group}>
          <h4>
            {group}
            <span>{list.length}</span>
          </h4>
          {list.map((entry) => (
            <button
              key={entry.id}
              type="button"
              className={entry.id === chosen ? 'editor-row on' : 'editor-row'}
              onClick={() => onOpen(entry)}
            >
              <span>{entry.name}</span>
              <span className="editor-row-id">{entry.id}</span>
            </button>
          ))}
        </div>
      ))}
    </div>
  );
}

export function EditorSpells({
  spells,
  world,
  onCommand,
}: {
  spells: readonly SpellWire[];
  world: World;
  onCommand: AdminCommand;
}): JSX.Element {
  const [draft, setDraft] = useState<SpellWire | null>(null);
  const books = useMemo(() => [...new Set(spells.map((spell) => spell.book))], [spells]);
  const set = (patch: Partial<SpellWire>): void =>
    setDraft((held) => (held ? { ...held, ...patch } : held));

  return (
    <>
      <MagicList
        entries={spells}
        groupOf={(spell) => spell.book}
        chosen={draft?.id ?? -1}
        onOpen={(spell) => setDraft(structuredClone(spell))}
        onNew={() => setDraft(blankSpell(books[0] ?? 'Combat'))}
        noun="Spell"
      />

      {draft ? (
        <div className="magic-page">
          <div className="magic-preview">
            <span className="book-cell">
              <Glyph icon={draft.icon} />
            </span>
            <strong>{draft.name}</strong>
            <span className="editor-row-id">
              {draft.id > 0 ? `id ${draft.id}` : 'not saved yet'}
            </span>
          </div>

          <h4>What it is</h4>
          <Text label="Name" value={draft.name} onChange={(name) => set({ name })} />
          <label className="editor-field">
            <span>Description</span>
            <textarea
              value={draft.description}
              maxLength={240}
              onChange={(event) => set({ description: event.target.value })}
            />
          </label>
          <div className="editor-brushes">
            <Text
              label="Book"
              value={draft.book}
              max={24}
              offered={books}
              onChange={(book) => set({ book })}
            />
            <Num
              label="Place in it"
              value={draft.order}
              max={999}
              onChange={(order) => set({ order })}
            />
          </div>
          <IconPicker icon={draft.icon} onChange={(icon) => set({ icon })} />

          <h4>What it asks for</h4>
          <div className="editor-brushes">
            <Num
              label="Magic level"
              value={draft.level}
              min={1}
              max={99}
              onChange={(level) => set({ level })}
            />
            <Num
              label="Experience"
              value={draft.experience}
              step={0.5}
              max={100000}
              onChange={(experience) => set({ experience })}
            />
          </div>
          <div className="editor-brushes">
            <Num label="Mana" value={draft.mana} max={10000} onChange={(mana) => set({ mana })} />
            <Num
              label="Ticks between casts"
              value={draft.speed}
              min={1}
              max={20}
              onChange={(speed) => set({ speed })}
            />
          </div>
          <h5>Used up on each cast</h5>
          {draft.components.map((part, at) => (
            <div className="editor-brushes" key={at}>
              <ItemPicker
                items={world.items}
                value={part.item}
                onPick={(item) =>
                  set({
                    components: draft.components.map((one, index) =>
                      index === at ? { ...one, item } : one,
                    ),
                  })
                }
              />
              <input
                type="number"
                min={1}
                max={1000}
                value={part.count}
                onChange={(event) =>
                  set({
                    components: draft.components.map((one, index) =>
                      index === at
                        ? {
                            ...one,
                            count: Math.max(1, Math.round(Number(event.target.value) || 1)),
                          }
                        : one,
                    ),
                  })
                }
              />
              <button
                type="button"
                className="editor-tool"
                onClick={() =>
                  set({ components: draft.components.filter((_, index) => index !== at) })
                }
              >
                ✕
              </button>
            </div>
          ))}
          {draft.components.length < 6 ? (
            <button
              type="button"
              className="editor-tab"
              onClick={() =>
                set({
                  components: [...draft.components, { item: world.items[0]?.id ?? 1, count: 1 }],
                })
              }
            >
              Add A Component
            </button>
          ) : null}
          <h5>And before it can be cast at all</h5>
          <Conditions
            held={draft.requires}
            items={world.items}
            quests={world.quests}
            characters={world.characters}
            objects={world.objects}
            onChange={(requires) => set({ requires: requires as unknown as SpellWire['requires'] })}
          />
          <button
            type="button"
            className="editor-tab"
            onClick={() => set({ requires: [...draft.requires, {}] })}
          >
            Add A Requirement
          </button>

          <h4>What it is cast at</h4>
          <label className="editor-field">
            <span>Target</span>
            <select
              value={draft.target}
              onChange={(event) => set({ target: event.target.value as SpellTargetWire })}
            >
              {TARGETS.map((one) => (
                <option key={one.value} value={one.value}>
                  {one.label}
                </option>
              ))}
            </select>
          </label>
          {draft.target !== 'self' && draft.target !== 'item' ? (
            <>
              <Num
                label="Reach (tiles)"
                value={draft.range}
                min={1}
                max={20}
                onChange={(range) => set({ range })}
              />
              <h5>The ground it covers</h5>
              <div className="editor-brushes">
                <label className="editor-field">
                  <span>Shape</span>
                  <select
                    value={draft.shape}
                    onChange={(event) => set({ shape: event.target.value as SpellShapeWire })}
                  >
                    {SHAPES.map((one) => (
                      <option key={one.value} value={one.value}>
                        {one.label}
                      </option>
                    ))}
                  </select>
                </label>
                <Num
                  label={draft.shape === 'line' ? 'Carries on past it' : 'Size'}
                  value={draft.radius}
                  max={10}
                  onChange={(radius) => set({ radius })}
                />
              </div>
              <ShapePreview shape={draft.shape} size={draft.radius} />
              <p className="hint">
                {draft.radius === 0 && draft.shape !== 'line'
                  ? 'Only the tile or the one it is cast at.'
                  : `${spellArea(draft.shape, draft.radius, { tx: 0, tz: 0, plane: 0 }, { tx: 0, tz: 5, plane: 0 }).length} tiles.`}
              </p>
            </>
          ) : null}
          <h5>What flies there</h5>
          <ProjectileFields
            projectile={draft.projectile}
            models={world.models}
            onChange={(projectile) => set({ projectile })}
          />
          <h5>What it sounds like</h5>
          <SoundPicker
            label="Cast"
            value={draft.sounds.cast}
            empty="The usual cast"
            onChange={(cast) => set({ sounds: { ...draft.sounds, cast } })}
          />
          <SoundPicker
            label="Landing"
            value={draft.sounds.impact}
            empty={
              draft.projectile.look || draft.projectile.model ? 'The usual landing' : 'Nothing'
            }
            onChange={(impact) => set({ sounds: { ...draft.sounds, impact } })}
          />
          {draft.damage ? (
            <label className="editor-field">
              <span>Cast from a staff in a fight</span>
              <input
                type="checkbox"
                checked={draft.autocast}
                onChange={(event) => set({ autocast: event.target.checked })}
              />
            </label>
          ) : null}

          <label
            className="editor-field"
            title="Insta-cast Spells do not interrupt your combat queue, and may be used at the same time as Auto-attacks."
          >
            <span>Insta-cast</span>
            <input
              type="checkbox"
              checked={draft.instant}
              onChange={(event) => set({ instant: event.target.checked })}
            />
          </label>

          <label
            className="editor-field"
            title="Which rung of the elemental ladder this is on, if any. The four elements of a rung are learned together."
          >
            <span>Rung</span>
            <input
              value={draft.tier}
              maxLength={16}
              placeholder="Strike, Bolt, Blast..."
              onChange={(event) => set({ tier: event.target.value })}
            />
          </label>

          <h4>What it does</h4>
          <label className="editor-field">
            <span>Does harm</span>
            <input
              type="checkbox"
              checked={draft.damage !== undefined}
              onChange={(event) =>
                set({
                  damage: event.target.checked
                    ? { max: 2, perLevels: 0, element: 'none' }
                    : undefined,
                  autocast: event.target.checked ? draft.autocast : false,
                })
              }
            />
          </label>
          {draft.damage ? (
            <div className="magic-box">
              <div className="editor-brushes">
                <Num
                  label="Hits for up to"
                  value={draft.damage.max}
                  max={1000}
                  onChange={(max) => draft.damage && set({ damage: { ...draft.damage, max } })}
                />
                <Num
                  label="Plus one every (levels)"
                  value={draft.damage.perLevels}
                  max={99}
                  onChange={(perLevels) =>
                    draft.damage && set({ damage: { ...draft.damage, perLevels } })
                  }
                />
              </div>
              <label className="editor-field">
                <span>Element</span>
                <select
                  value={draft.damage.element}
                  onChange={(event) =>
                    draft.damage &&
                    set({ damage: { ...draft.damage, element: event.target.value } })
                  }
                >
                  {ELEMENTS.map((one) => (
                    <option key={one} value={one}>
                      {one === 'none' ? 'None' : one[0]?.toUpperCase() + one.slice(1)}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          ) : null}
          <FormulaField
            label="Chance it lands"
            value={draft.accuracy}
            empty="The usual roll: magic against their defence"
            onChange={(accuracy) => set({ accuracy })}
          />
          <Num label="Heals" value={draft.heals} max={1000} onChange={(heals) => set({ heals })} />
          {draft.restores.map((one, at) => (
            <div className="editor-brushes" key={at}>
              <span>Restores</span>
              <select
                value={one.of}
                onChange={(event) =>
                  set({
                    restores: draft.restores.map((entry, index) =>
                      index === at
                        ? { ...entry, of: event.target.value as 'mana' | 'run' | 'faith' }
                        : entry,
                    ),
                  })
                }
              >
                <option value="mana">Mana</option>
                <option value="run">Run energy</option>
                <option value="faith">Faith</option>
              </select>
              <input
                type="number"
                min={1}
                max={10000}
                value={one.amount}
                onChange={(event) =>
                  set({
                    restores: draft.restores.map((entry, index) =>
                      index === at
                        ? {
                            ...entry,
                            amount: Math.max(1, Math.round(Number(event.target.value) || 1)),
                          }
                        : entry,
                    ),
                  })
                }
              />
              <button
                type="button"
                className="editor-tool"
                onClick={() => set({ restores: draft.restores.filter((_, index) => index !== at) })}
              >
                ✕
              </button>
            </div>
          ))}
          {draft.restores.length < 3 ? (
            <button
              type="button"
              className="editor-tab"
              onClick={() => set({ restores: [...draft.restores, { of: 'mana', amount: 10 }] })}
            >
              Add Something It Restores
            </button>
          ) : null}

          <label className="editor-field">
            <span>Leaves something lasting</span>
            <input
              type="checkbox"
              checked={draft.status !== undefined}
              onChange={(event) =>
                set({
                  status: event.target.checked
                    ? {
                        name: draft.name,
                        ticks: 10,
                        modifiers: [],
                        stun: false,
                        bind: false,
                        poison: 0,
                        every: 5,
                        helpful: false,
                        chance: 'hit',
                      }
                    : undefined,
                })
              }
            />
          </label>
          {draft.status ? (
            <StatusFields status={draft.status} onChange={(status) => set({ status })} />
          ) : null}

          {draft.target === 'item' ? (
            <div className="magic-box">
              <label className="editor-field">
                <span>Uses the item up</span>
                <input
                  type="checkbox"
                  checked={draft.item?.consumes ?? true}
                  onChange={(event) =>
                    set({
                      item: {
                        consumes: event.target.checked,
                        coinsPercent: draft.item?.coinsPercent ?? 0,
                      },
                    })
                  }
                />
              </label>
              <Num
                label="Coins for it (% of its value)"
                value={draft.item?.coinsPercent ?? 0}
                max={500}
                onChange={(coinsPercent) =>
                  set({ item: { consumes: draft.item?.consumes ?? true, coinsPercent } })
                }
              />
            </div>
          ) : null}

          <h5>And anything else, for the caster</h5>
          <p className="hint">Teleports go here: pick the spot where it lands in the world.</p>
          <Effects
            held={draft.effects}
            items={world.items}
            quests={world.quests}
            shops={world.shops}
            characters={world.characters}
            objects={world.objects}
            onChange={(effects) => set({ effects: effects as unknown as SpellWire['effects'] })}
          />
          <button
            type="button"
            className="editor-tab"
            onClick={() => set({ effects: [...draft.effects, { says: '' }] })}
          >
            Add An Effect
          </button>

          <div className="editor-brushes magic-save">
            <button
              type="button"
              className="primary"
              onClick={() => onCommand('saveSpell', {}, { json: JSON.stringify(draft) })}
            >
              Save
            </button>
            <button
              type="button"
              onClick={() =>
                setDraft({ ...structuredClone(draft), id: 0, name: `${draft.name} Copy` })
              }
            >
              Copy
            </button>
            {draft.id > 0 ? (
              <button
                type="button"
                onClick={() => {
                  if (!window.confirm(`Take ${draft.name} out of the book for everybody?`)) return;
                  onCommand('removeSpell', { id: draft.id }, {});
                  setDraft(null);
                }}
              >
                Delete
              </button>
            ) : null}
          </div>
        </div>
      ) : (
        <p className="hint">Choose a spell to change it, or start a new one.</p>
      )}
    </>
  );
}

export function EditorPrayers({
  prayers,
  world,
  onCommand,
}: {
  prayers: readonly PrayerWire[];
  world: World;
  onCommand: AdminCommand;
}): JSX.Element {
  const [draft, setDraft] = useState<PrayerWire | null>(null);
  const kinds = useMemo(() => [...new Set(prayers.map((prayer) => prayer.category))], [prayers]);
  const groups = useMemo(
    () => [...new Set(prayers.map((prayer) => prayer.group).filter(Boolean))],
    [prayers],
  );
  const set = (patch: Partial<PrayerWire>): void =>
    setDraft((held) => (held ? { ...held, ...patch } : held));
  const sharing =
    draft && draft.group
      ? prayers.filter((one) => one.group === draft.group && one.id !== draft.id)
      : [];

  return (
    <>
      <MagicList
        entries={prayers}
        groupOf={(prayer) => prayer.category}
        chosen={draft?.id ?? -1}
        onOpen={(prayer) => setDraft(structuredClone(prayer))}
        onNew={() => setDraft(blankPrayer(kinds[0] ?? 'Prayers'))}
        noun="Prayer"
      />

      {draft ? (
        <div className="magic-page">
          <div className="magic-preview">
            <span className="book-cell">
              <Glyph icon={draft.icon} />
            </span>
            <strong>{draft.name}</strong>
            <span className="editor-row-id">
              {draft.id > 0 ? `id ${draft.id}` : 'not saved yet'}
            </span>
          </div>

          <h4>What it is</h4>
          <Text label="Name" value={draft.name} onChange={(name) => set({ name })} />
          <label className="editor-field">
            <span>Description</span>
            <textarea
              value={draft.description}
              maxLength={240}
              onChange={(event) => set({ description: event.target.value })}
            />
          </label>
          <div className="editor-brushes">
            <Text
              label="Kind"
              value={draft.category}
              max={24}
              offered={kinds}
              onChange={(category) => set({ category })}
            />
            <Num
              label="Place in it"
              value={draft.order}
              max={999}
              onChange={(order) => set({ order })}
            />
          </div>
          <IconPicker icon={draft.icon} onChange={(icon) => set({ icon })} />

          <h4>What it asks for</h4>
          <div className="editor-brushes">
            <Num
              label="Faith level"
              value={draft.level}
              min={1}
              max={99}
              onChange={(level) => set({ level })}
            />
            <Num
              label="Faith a minute"
              value={draft.drain}
              step={0.5}
              max={600}
              onChange={(drain) => set({ drain })}
            />
          </div>
          <Conditions
            held={draft.requires}
            items={world.items}
            quests={world.quests}
            characters={world.characters}
            objects={world.objects}
            onChange={(requires) =>
              set({ requires: requires as unknown as PrayerWire['requires'] })
            }
          />
          <button
            type="button"
            className="editor-tab"
            onClick={() => set({ requires: [...draft.requires, {}] })}
          >
            Add A Requirement
          </button>

          <h4>Held with others</h4>
          <Text
            label="Group"
            value={draft.group}
            max={24}
            offered={groups}
            onChange={(group) => set({ group: group.trim() })}
          />
          <p className="hint">
            {draft.group
              ? sharing.length > 0
                ? `Taking it up puts down ${sharing.map((one) => one.name).join(', ')}.`
                : 'Nothing else is in this group yet.'
              : 'In no group: it can be held alongside anything.'}
          </p>

          <h4>What it does while it is held</h4>
          <ModifierRows held={draft.modifiers} onChange={(modifiers) => set({ modifiers })} />

          <div className="editor-brushes magic-save">
            <button
              type="button"
              className="primary"
              onClick={() => onCommand('savePrayer', {}, { json: JSON.stringify(draft) })}
            >
              Save
            </button>
            <button
              type="button"
              onClick={() =>
                setDraft({ ...structuredClone(draft), id: 0, name: `${draft.name} Copy` })
              }
            >
              Copy
            </button>
            {draft.id > 0 ? (
              <button
                type="button"
                onClick={() => {
                  if (!window.confirm(`Take ${draft.name} away from everybody?`)) return;
                  onCommand('removePrayer', { id: draft.id }, {});
                  setDraft(null);
                }}
              >
                Delete
              </button>
            ) : null}
          </div>
        </div>
      ) : (
        <p className="hint">Choose a prayer to change it, or start a new one.</p>
      )}
    </>
  );
}
